import { IconSearch } from "../app/icons";
import { EmptyState } from "../app/Status";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import type { DossierSummaryDto } from "@klantpunt/shared";
import { PageHeader } from "../app/PageHeader";
import { buildingPath } from "../app/navItems";
import { errMsg } from "../features/dossier/DossierDialogs";
import { usageText } from "../features/dossier/UsageFunctions";
import { api } from "../lib/api";
import { hasModule, useMe } from "../lib/auth";

const Count = ({ n, cls }: { n: number; cls: string }) =>
  n ? <span className={`chip dir-cert dir-cert-${cls}`}>{n}</span> : <span className="muted">–</span>;

/** Dossiers over alle gebouwen: wat ontbreekt, wat verloopt en wat al verlopen is (docs/DOSSIER.md, D2). */
export function DossiersPage() {
  const { data: me } = useMe();
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [q, setQ] = useState("");
  const list = useQuery({
    queryKey: ["dossiers"],
    queryFn: () => api<DossierSummaryDto[]>("/dossiers"),
    enabled: hasModule(me, "dossier"),
  });
  if (!hasModule(me, "dossier"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Dossier.</p>
      </div>
    );
  const needle = q.trim().toLowerCase();
  const all = list.data ?? [];
  const attention = (x: DossierSummaryDto) => x.missing + x.expiring + x.expired > 0;
  const rows = all.filter(
    (x) =>
      (!onlyAttention || attention(x)) &&
      (!needle || `${x.building.code} ${x.building.name} ${x.building.city ?? ""}`.toLowerCase().includes(needle)),
  );
  const sum = (k: "missing" | "expiring" | "expired") => all.reduce((n, x) => n + x[k], 0);
  return (
    <div className="page dir-page">
      <PageHeader label="Alle gebouwen" title="Dossiers" />
      <p className="muted small">
        Per gebouw de documenten die bij de gebruiksfunctie horen en wat binnenkort verloopt. Herinneringen komen ook in
        de Inbox.
      </p>
      <div className="dir-filters">
        <input
          type="search"
          className="dir-search"
          aria-label="Zoeken op gebouw"
          placeholder="Zoeken op code, naam of plaats"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="dir-kinds" role="group" aria-label="Filter">
          <button type="button" className="kpi" aria-pressed={!onlyAttention} onClick={() => setOnlyAttention(false)}>
            Alle gebouwen <span className="count num">{all.length}</span>
          </button>
          <button type="button" className="kpi" aria-pressed={onlyAttention} onClick={() => setOnlyAttention(true)}>
            Vraagt aandacht <span className="count num">{all.filter(attention).length}</span>
          </button>
        </div>
        <span className="muted small">
          {sum("missing")} ontbreken · {sum("expiring")} verlopen binnenkort · {sum("expired")} verlopen
        </span>
      </div>
      {list.isError && <p className="alert">{errMsg(list.error)}</p>}
      <div className="tscroll">
        <table className="it it-list" aria-label="Dossiers" aria-busy={list.isPending}>
          <thead>
            <tr>
              <th scope="col">Gebouw</th>
              <th scope="col" className="dir-hide-narrow">
                Gebruiksfunctie
              </th>
              <th scope="col" className="num dir-hide-narrow">
                Documenten
              </th>
              <th scope="col" className="num">
                Ontbreekt
              </th>
              <th scope="col" className="num">
                Verloopt
              </th>
              <th scope="col" className="num">
                Verlopen
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={x.building.id}>
                <td className="tcell-title">
                  <Link to={`${buildingPath(x.building.id, "dossier")}${x.missing ? "?tab=verplicht" : ""}`}>
                    <span className="code">{x.building.code}</span> {x.building.name}
                  </Link>
                  {x.building.city && <div className="muted small">{x.building.city}</div>}
                </td>
                <td className="dir-hide-narrow small">
                  {x.usageFunctions.length ? usageText(x.usageFunctions) : <span className="muted">Niet ingevuld</span>}
                </td>
                <td className="num dir-hide-narrow">{x.documents}</td>
                <td className="num" data-label="ontbreekt">
                  <Count n={x.missing} cls="expired" />
                </td>
                <td className="num" data-label="verloopt">
                  <Count n={x.expiring} cls="warn" />
                </td>
                <td className="num" data-label="verlopen">
                  <Count n={x.expired} cls="expired" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && !rows.length && (
        <EmptyState icon={IconSearch} title="Geen gebouwen gevonden" text="Pas de zoekterm of het filter aan." />
      )}
    </div>
  );
}
